import { BigDecimal } from "effect"
import { formatMoney, formatPercentOf, formatPrice, formatQty, formatSignedMoney } from "../trading/format.ts"
import type { BoxLabels } from "./tradeBoxPrimitive.ts"

// The texts TradingView puts on its long / short position tool (docs/design/0016): for the target
// and the stop, their distance from the entry (price, percent, ticks) and the amount made or lost
// there; at the entry, the trade's P&L (open or closed), its quantity and the risk / reward ratio.

type Decimal = BigDecimal.BigDecimal

/** One target: its price and the part of the trade it closes. */
export interface BoxTarget {
  readonly price: number
  readonly qty: Decimal
}

export interface BoxLabelInput {
  readonly entry: number
  readonly stopLoss: number | null
  /** The take-profits, nearest the entry first (several: docs/design/0017). */
  readonly takeProfits: ReadonlyArray<BoxTarget>
  readonly qty: Decimal
  readonly pnl: Decimal
  readonly pnlName: "Open P&L" | "Closed P&L"
}

/**
 * "1.9": the targets' distance over the stop's, from the entry, each weighted by the part it closes
 * (one target: its R multiple); null without a stop-loss and a target.
 */
export const riskRewardRatio = (entry: number, stopLoss: number | null, takeProfits: ReadonlyArray<BoxTarget>): string | null => {
  if (stopLoss === null || takeProfits.length === 0) return null
  const risk = Math.abs(entry - stopLoss)
  const total = takeProfits.reduce((sum, target) => sum + BigDecimal.toNumberUnsafe(target.qty), 0)
  if (risk === 0 || total <= 0) return null
  const reward = takeProfits.reduce((sum, target) => sum + Math.abs(target.price - entry) * BigDecimal.toNumberUnsafe(target.qty), 0) / total
  return String(Number((reward / risk).toFixed(2)))
}

/** `tickSize` null (a backtest, which has no instrument): no tick counts, prices to 2 decimals. */
export const boxLabels = (input: BoxLabelInput, tickSize: Decimal | null, currency: string): BoxLabels => {
  const precision = tickSize === null ? 2 : Math.max(0, BigDecimal.normalize(tickSize).scale)
  const tick = tickSize === null ? 0 : BigDecimal.toNumberUnsafe(tickSize)
  const entry = BigDecimal.fromNumberUnsafe(input.entry)
  const leg = (name: string, price: number, qty: Decimal, showQty: boolean): string => {
    const distance = Math.abs(price - input.entry)
    const percent = formatPercentOf(BigDecimal.fromNumberUnsafe(distance), entry).replace(/^\+/, "")
    const ticks = tick > 0 ? ` ${Math.round(distance / tick).toLocaleString("en-US")} ticks` : ""
    const size = Math.abs(BigDecimal.toNumberUnsafe(qty))
    const amount = formatMoney(BigDecimal.fromNumberUnsafe(Number((distance * size).toFixed(8))), currency)
    const part = showQty ? `, Qty: ${formatQty(BigDecimal.abs(qty), currency)}` : ""
    return `${name}: ${formatPrice(BigDecimal.fromNumberUnsafe(distance), precision, currency)} (${percent})${ticks}${part}, Amount: ${amount}`
  }
  const several = input.takeProfits.length > 1
  const ratio = riskRewardRatio(input.entry, input.stopLoss, input.takeProfits)
  return {
    reward: several || input.takeProfits[0] === undefined ? null : leg("Target", input.takeProfits[0].price, input.qty, false),
    // Several targets: one label each, at its own line, with the part it closes.
    targets: several ? input.takeProfits.map((target, i) => ({ price: target.price, text: leg(`TP${i + 1}`, target.price, target.qty, true) })) : [],
    risk: input.stopLoss === null ? null : leg("Stop", input.stopLoss, input.qty, false),
    entry: [
      `${input.pnlName}: ${formatSignedMoney(input.pnl, currency)}, Qty: ${formatQty(BigDecimal.abs(input.qty), currency)}`,
      ...(ratio === null ? [] : [`Risk/reward ratio: ${ratio}`])
    ],
    entryWinning: !BigDecimal.isNegative(input.pnl)
  }
}
